import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label'; //
import { ArrowLeft, Plus, Trash2, Sparkles, Pencil, Crown, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { canAddChild, getChildProfileLimit, canAccessStatistics } from '@/utils/subscriptionUtils';
import GuestModeBanner from '@/components/GuestModeBanner';
import { READING_LEVELS, getLevel } from '@/utils/readingLevels';


//const STORY_STYLES = ['random','adventurous', 'cultural', 'facts and figures', 'fantasy', 'funny', 'gentle', 'historical', 'inspirational', 'magical', 'mystery', 'philosophical', 'realistic', 'sci-fi', 'suspense'];

const INTEREST_CATEGORIES = {
  'Creative Arts': ['baking', 'cooking', 'dance', 'drawing', 'music', 'painting', 'photography', 'sculpting', 'singing', 'theater', 'writing'],
  'Fantasy & Adventure': ['dragons', 'fairies', 'magic', 'mermaids', 'pirates', 'superheroes', 'unicorns', 'wizards'],
  'Nature & Animals': ['birds', 'dinosaurs', 'farm', 'flowers', 'insects', 'ocean', 'pets', 'wildlife', 'reptiles', 'jungle'],
  'Science & Tech': ['coding', 'experiments', 'planets', 'robots', 'space', 'technology', 'volcanoes', 'inventions', 'engineering'],
  'Sports & Activities': ['basketball', 'camping', 'hiking', 'soccer', 'sports', 'swimming', 'tennis', 'cycling', 'skateboarding'],
  'Vehicles & Transport': ['airplanes', 'boats', 'cars', 'excavators', 'helicopters', 'motorcycles', 'trains', 'trucks', 'spaceships']
};

const GENDERS = ['male', 'female', 'neutral'];
const HAIR_OPTIONS = ['black', 'blue', 'blonde', 'brown', 'green', 'grey', 'pink', 'purple', 'red', 'white'];
const HAIR_STYLE_OPTIONS = ['braided', 'curly', 'long', 'ponytail', 'short', 'straight', 'wavy'];
const SKIN_OPTIONS = ['African', 'East Asian', 'Southeast Asian', 'European', 'Middle Eastern', 'Latin American', 'Pacific Islander'];
const EYE_OPTIONS = ['blue', 'green', 'brown', 'hazel'];
const CLOTHING_OPTIONS = ['business','casual wear','cocktail dress','coat and jacket','dress','ethnic costume','evening gown','festival outfit','formal wear','jeans and t-shirt','overalls','party wear','pyjamas','rainwear','school uniform','sporty outfit','summer wear','trades wear','traditional attire','winter wear'];

const ChildManager = () => {
  const navigate = useNavigate();
  const { user, token, nightMode, refreshUser } = useContext(AuthContext);
  const [children, setChildren] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  
  const userTier = user?.subscription_tier || 'free';
  const childLimit = getChildProfileLimit(userTier);
  const canAddMore = canAddChild(userTier, children.length);
  const canViewStats = canAccessStatistics(userTier);
  const isGuestMode = user?.id === 'guest-user';
  const [formData, setFormData] = useState({
    name: '',
    reading_level: 'entry',
    story_styles: ['random'],
    interests: [],
    attributes: {
      gender: 'female',
      hair: '',
      hair_style: '',
      skin: '',
      eyes: '',
      clothing: ''
    }
  });
  const [generating, setGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [otherInterest, setOtherInterest] = useState('');

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      // Use metadata endpoint with thumbnails for faster loading
      const response = await axios.get(`${API}/children/metadata`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
      // A 404 is expected for new users with no children, treat as empty list.
      if (error.response?.status !== 404) {
        toast.error('Failed to load children');
      }
      setChildren([]);
     }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedChild) {
        await axios.patch(`${API}/children/${selectedChild.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Child updated successfully');
      } else {
        await axios.post(`${API}/children`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Child created successfully');
      }
      setShowDialog(false);
      fetchChildren();
      resetForm();
    } catch (error) {
      toast.error('Failed to save child');
    }
  };

  const handleDelete = async (childId) => {
    if (!window.confirm('Are you sure you want to delete this child profile?')) return;
    
    try {
      await axios.delete(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Child deleted successfully');
      fetchChildren();
    } catch (error) {
      toast.error('Failed to delete child');
    }
  };

  const handleGenerateImage = async (childId) => {
    setGenerating(true);
    try {
      // Find the specific child's data from the state to send as a payload
      const childToGenerate = children.find(c => c.id === childId) || selectedChild;
if (!childToGenerate) {
        toast.error("Could not find child data to generate image.");
        setGenerating(false);
        return;
      }

      const response = await axios.post(
        `${API}/children/${childId}/generate-image`,
        childToGenerate, // Send the full child object as the payload
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Image generated successfully!');
      
      // Update the image in the local state for a more responsive UI
      const newImageUrl = response.data.image;
      setChildren(prev => prev.map(c => c.id === childId ? { ...c, profile_image: newImageUrl } : c));
      setSelectedChild(prev => prev && prev.id === childId ? { ...prev, profile_image: newImageUrl } : prev);

    } catch (error) {
      toast.error('Failed to generate image' + (error.response?.data?.detail ? `: ${error.response.data.detail}` : ''));
    } finally {
      setGenerating(false);
    }
  };

  const openDialog = (child = null) => {
    // Check if user can add more children (only when adding new, not editing)
    if (!child && !canAddMore) {
      toast.error(`You've reached your limit of ${childLimit} child profile${childLimit > 1 ? 's' : ''} for ${userTier} tier`, {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/subscription')
        },
        duration: 5000
      });
      return;
    }
    
    if (child) {
      setSelectedChild(child);
      setFormData({
        name: child.name,
        reading_level: getLevel(child.reading_level),
        story_styles: child.story_styles || [],
        interests: child.interests || [],
        attributes: child.attributes || {
          gender: 'female',
          hair: '',
          hair_style: '',
          skin: '',
          eyes: '',
          clothing: ''
        }
      });
    }
    setShowDialog(true);
  };

  const resetForm = () => {
    setSelectedChild(null);
    setSelectedCategory('');
    setOtherInterest('');
    setFormData({
      name: '',
      reading_level: 'entry',
      story_styles: ['random'],
      interests: [],
      attributes: {
        gender: 'female',
        hair: '',
        hair_style: '',
        skin: '',
        eyes: '',
        clothing: ''
      }
    });
  };

  const updateAttribute = (key, value) => {
    setFormData(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [key]: value }
    }));
  };

  const toggleSelection = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleOtherInterestAdd = () => {
    const trimmed = otherInterest.trim().toLowerCase();
    if (!trimmed) {
      toast.error('Please enter an interest');
      return;
    }
    
    // Basic validation - check for invalid characters
    const allowedCharsPattern = /^[a-zA-Z0-9\s,.!;-]*$/;
    if (!allowedCharsPattern.test(trimmed)) {
      toast.error('Only letters, numbers, and ,.!;- are allowed');
      return;
    }
    
    // Check if already exists
    if (formData.interests.includes(trimmed)) {
      toast.error('This interest is already added');
      return;
    }
    
    // Add to interests
    setFormData(prev => ({
      ...prev,
      interests: [...prev.interests, trimmed]
    }));
    setOtherInterest('');
    toast.success('Interest added');
  };

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {isGuestMode && <GuestModeBanner />}
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="rounded-full flex-shrink-0 w-20"
              style={{
                background: nightMode ? 'var(--bg-secondary)' : 'white',
                color: nightMode ? 'var(--text-primary)' : 'inherit',
                borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
              }}
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
              Manage Children
            </h1>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <Button
            data-testid="add-new-child-btn"
            onClick={() => openDialog()}
            disabled={isGuestMode || !canAddMore}
            className={`pill-button ${!canAddMore || isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ background: 'var(--accent-2, #818cf8)', color: 'var(--accent-foreground, white)' }}
            title={isGuestMode ? 'Sign in to add children' : !canAddMore ? 'Upgrade to add more' : ''}
          >
            <Plus className="mr-2 w-5 h-5" />
            Add New Child {!canAddMore && <Crown className="ml-2 w-4 h-4" />}
          </Button>
          {!canAddMore && !isGuestMode && (
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              You&apos;ve reached your limit of {childLimit} child profile{childLimit > 1 ? 's' : ''}. Upgrade to add more.
            </p>
          )}
          {isGuestMode && (
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Sign in to create and manage children profiles
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {children.map((child) => (
            <div key={child.id} className="story-card p-2 sm:p-3 flex flex-col" data-testid={`child-profile-${child.id}`}>
              {child.profile_image ? (
                <div className="mb-2">
                  <img
                    src={child.profile_image.startsWith('http') 
                      ? child.profile_image 
                      : `data:image/png;base64,${child.profile_image}`}
                    alt={child.name}
                    className="w-full h-24 sm:h-auto sm:aspect-square rounded-lg sm:rounded-full object-cover mx-auto xl:w-32 xl:h-32"
                  />
                </div>
              ) : (
                <div className="mb-2">
                  <div className="w-full h-24 sm:h-auto sm:aspect-square rounded-lg sm:rounded-full flex items-center justify-center mx-auto xl:w-32 xl:h-32" style={{ background: 'var(--accent-1)' }}>
                    <span className="text-2xl sm:text-3xl text-white font-bold">{child.name.charAt(0)}</span>
                  </div>
                </div>
              )}
              
              <div className="flex-grow">
                <h3 className="text-sm sm:text-base font-bold mb-0.5 line-clamp-1" style={{ color: 'var(--text-primary)' }}>{child.name}</h3>
                <p className="text-xs mb-1.5 sm:mb-2 hidden sm:block capitalize" style={{ color: 'var(--text-secondary)' }}>Level: {getLevel(child.reading_level)}</p>
                
                {child.interests.length > 0 && (
                  <div className="mb-1.5 sm:mb-2 hidden sm:block">
                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Interests:</p>
                      <div className="flex flex-wrap gap-1">
                        {child.interests.slice(0, 3).map((interest, idx) => (
                          <span key={idx} className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'var(--accent-1)', color: 'white' }}>
                            {interest}
                          </span>
                        ))}
                      </div>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-center gap-1 sm:gap-1.5 mt-auto pt-2">
                <Button
                  data-testid={`view-profile-btn-${child.id}`}
                  onClick={() => {
                    if (!canViewStats) {
                      toast.error('Learning profile is available for Premium and Family tiers', {
                        action: {
                          label: 'Upgrade',
                          onClick: () => navigate('/subscription')
                        }
                      });
                      return;
                    }
                    navigate(`/children/${child.id}`);
                  }}
                  variant="default"
                  className={`rounded-full relative ${!canViewStats ? 'opacity-50' : ''}  px-2 py-1 sm:px-3 sm:py-1.5 gap-1 sm:gap-1.5`}
                  size="sm"
                  title={canViewStats ? 'Learning Profile' : 'Upgrade to Premium for learning profile'}
                >
                  <ListChecks className="w-4 h-4" />
                  <span className="hidden lg:inline ml-1">Learning</span>
                  {!canViewStats && <Crown className="inline w-3 h-3 ml-1" />}
                </Button>

                
                <Button
                  data-testid={`generate-child-image-tile-${child.id}`}
                  onClick={async () => {
                    setGenerating(true);
                    await handleGenerateImage(child.id);
                    setGenerating(false);
                  }}
                  disabled={generating || isGuestMode}
                  variant="outline"
                  size="sm"
                  className={`rounded-full flex items-center px-2 py-1 sm:px-3 sm:py-1.5 gap-1 sm:gap-1.5 ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''} sm:px-3`}
                  style={{
                    background: nightMode ? 'var(--bg-secondary)' : 'white',
                    color: nightMode ? 'var(--text-primary)' : 'inherit',
                    borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
                  }}
                  title={isGuestMode ? 'Sign in to generate images' : 'Generate Image'}
                >
                  <Sparkles className={`w-3 h-3 sm:w-4 sm:h-4 ${generating ? 'animate-spin' : ''}`} />
                  <span className="hidden lg:inline">Image</span>
                </Button>

                
                <Button
                  data-testid={`edit-child-btn-${child.id}`}
                  onClick={() => openDialog(child)}
                  variant="outline"
                  size="icon"
                  className={`rounded-full flex items-center gap-1`}
                  style={{
                    background: nightMode ? 'var(--bg-secondary)' : 'white',
                    color: nightMode ? 'var(--text-primary)' : 'inherit',
                    borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
                  }}
                  title="Edit Profile"
                >
                  <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
               

                <Button
                  data-testid={`delete-child-btn-${child.id}`}
                  onClick={() => handleDelete(child.id)}
                  disabled={isGuestMode}
                  variant="destructive"
                  size="icon"
                  className={`rounded-full ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={isGuestMode ? 'Sign in to delete' : ''}
                >
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Child Form Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {selectedChild ? 'Edit Child' : 'Add New Child'}
            </DialogTitle>
            <DialogDescription>
              Fill in the details to create or update a child's profile. This helps in personalizing stories and generating images.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {selectedChild && selectedChild.profile_image && (
              <div className="flex justify-center mb-4">
                <img
                  src={selectedChild.profile_image.startsWith('http')
                    ? selectedChild.profile_image
                    : `data:image/png;base64,${selectedChild.profile_image}`}
                  alt={selectedChild.name}
                  className="w-32 h-32 rounded-full object-cover"
                />
              </div>
            )}
            <div>
              <Label htmlFor="name">Child&apos;s Name</Label>
              <Input
                data-testid="child-name-input"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Reading Level</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {READING_LEVELS.map((level) => (
                  <button
                    key={level}
                    type="button"
                    data-testid={`reading-level-${level}`}
                    onClick={() => setFormData({ ...formData, reading_level: level })}
                    className={`tag-button ${formData.reading_level === level ? 'selected' : ''}`}
                    style={{
                      background: formData.reading_level === level ? 'var(--accent-2)' : 'var(--bg-secondary)',
                      color: formData.reading_level === level ? 'white' : 'var(--text-primary)'
                    }}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Interests (select multiple)</Label>
              
              {/* Category Selection */}
              <div className="flex flex-wrap gap-1 mt-3 mb-3">
                {Object.keys(INTEREST_CATEGORIES).map((category) => (
                  <button
                    key={category}
                    type="button"
                    data-testid={`interest-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)}
                    className={`tag-button ${selectedCategory === category ? 'selected' : ''}`}
                    style={{
                      background: selectedCategory === category ? 'var(--accent-1)' : 'var(--bg-secondary)',
                      color: selectedCategory === category ? 'white' : 'var(--text-primary)',
                      fontWeight: '600'
                    }}
                  >
                    {category}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="interest-category-other"
                  onClick={() => setSelectedCategory(selectedCategory === 'Other' ? '' : 'Other')}
                  className={`tag-button ${selectedCategory === 'Other' ? 'selected' : ''}`}
                  style={{
                    background: selectedCategory === 'Other' ? 'var(--accent-1)' : 'var(--bg-secondary)',
                    color: selectedCategory === 'Other' ? 'white' : 'var(--text-primary)',
                    fontWeight: '600'
                  }}
                >
                  Other
                </button>
              </div>

              {/* Sub-groups - shown when category is selected */}
              {selectedCategory && selectedCategory !== 'Other' && (
                <div 
                  className="p-3 rounded-lg mb-3" 
                  style={{ 
                    border: '2px solid var(--accent-2)', 
                    background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)' 
                  }}
                >
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Select from {selectedCategory}:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {INTEREST_CATEGORIES[selectedCategory].map((interest) => (
                      <button
                        key={interest}
                        type="button"
                        data-testid={`interest-${interest}`}
                        onClick={() => toggleSelection('interests', interest)}
                        className={`tag-button ${formData.interests.includes(interest) ? 'selected' : ''}`}
                        style={{
                          background: formData.interests.includes(interest) ? 'var(--accent-2)' : 'var(--bg-secondary)',
                          color: formData.interests.includes(interest) ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Other - free text input */}
              {selectedCategory === 'Other' && (
                <div 
                  className="p-3 rounded-lg mb-3" 
                  style={{ 
                    border: '2px solid var(--accent-2)', 
                    background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)' 
                  }}
                >
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Enter custom interest:
                  </p>
                  <div className="flex gap-1">
                    <Input
                      data-testid="other-interest-input"
                      placeholder="e.g., pottery, origami..."
                      value={otherInterest}
                      onChange={(e) => setOtherInterest(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleOtherInterestAdd();
                        }
                      }}
                      maxLength={50}
                    />
                    <Button
                      type="button"
                      data-testid="add-other-interest-btn"
                      onClick={handleOtherInterestAdd}
                      variant="outline"
                      size="sm"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}

              {/* Selected Interests Display */}
              {formData.interests.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Selected Interests:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {formData.interests.map((interest, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1 rounded-full flex items-center gap-1"
                        style={{ background: 'var(--accent-2)', color: 'white' }}
                      >
                        {interest}
                        <button
                          type="button"
                          onClick={() => toggleSelection('interests', interest)}
                          className="ml-1 hover:text-red-200"
                          aria-label="Remove interest"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Gender</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {GENDERS.map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    data-testid={`gender-${gender}`}
                    onClick={() => updateAttribute('gender', gender)}
                    className={`tag-button ${formData.attributes.gender === gender ? 'selected' : ''}`}
                  >
                    {gender.charAt(0).toUpperCase() + gender.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Hair</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {HAIR_OPTIONS.map((hair) => (
                  <button
                    key={hair}
                    type="button"
                    data-testid={`hair-${hair}`}
                    onClick={() => updateAttribute('hair', hair)}
                    className={`tag-button ${formData.attributes.hair === hair ? 'selected' : ''}`}
                  >
                    {hair}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Hair Style</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {HAIR_STYLE_OPTIONS.map((style) => (
                  <button
                    key={style}
                    type="button"
                    data-testid={`hair-style-${style}`}
                    onClick={() => updateAttribute('hair_style', style)}
                    className={`tag-button ${formData.attributes.hair_style === style ? 'selected' : ''}`}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Eye Color</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {EYE_OPTIONS.map((eye) => (
                  <button
                    key={eye}
                    type="button"
                    data-testid={`eye-${eye}`}
                    onClick={() => updateAttribute('eyes', eye)}
                    className={`tag-button ${formData.attributes.eyes === eye ? 'selected' : ''}`}
                  >
                    {eye}
                  </button>
                ))}
              </div>
            </div>

            <div>              
              <Label>Cultural Heritage</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {SKIN_OPTIONS.map((skin) => (
                  <button
                    key={skin}
                    type="button"
                    data-testid={`skin-${skin}`}
                    onClick={() => updateAttribute('skin', skin)}
                    className={`tag-button ${formData.attributes.skin === skin ? 'selected' : ''}`}
                  >
                    {skin}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Clothing</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {CLOTHING_OPTIONS.map((clothing) => (
                  <button
                    key={clothing}
                    type="button"
                    data-testid={`clothing-${clothing}`}
                    onClick={() => updateAttribute('clothing', clothing)}
                    className={`tag-button ${formData.attributes.clothing === clothing ? 'selected' : ''}`}
                  >
                    {clothing}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-1">
              <Button
                data-testid="generate-child-image-btn"
                type="button"
                onClick={async () => {
                  if (selectedChild) {
                    setGenerating(true);
                    await handleGenerateImage(selectedChild.id);
                    setGenerating(false);
                  } else {
                    toast.error('Please save the child first before generating image');
                  }
                }}
                disabled={generating || !selectedChild || isGuestMode}
                variant="outline"
                className={`flex-1 rounded-full ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isGuestMode ? 'Sign in to generate images' : ''}
              >
                {generating ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1" />
                    Generate Image
                  </>
                )}
              </Button>
              <Button
                data-testid="save-child-btn"
                type="submit"
                disabled={isGuestMode}
                className={`flex-1 pill-button ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ background: 'var(--accent-2)', color: 'white' }}
                title={isGuestMode ? 'Sign in to save children' : ''}
              >
                {selectedChild ? 'Update Child' : 'Create Child'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChildManager;